import { t } from "@lingui/macro"
import { makeAutoObservable } from "mobx"
import React from "react"

import {
    CommunityAdminService,
    PollAdminService,
    PreviewService,
    community_CommunityForAdmin,
    community_PostWithAdminDetailsV2,
} from "src/api"
import { Channel } from "src/channel"
import { loads } from "src/channel/utils"
import { DEFAULT_ACCESS_GROUP, environment } from "src/config"
import { range } from "src/lib/array"
import { getAuthorName } from "src/lib/community"
import { isDateInPast } from "src/lib/date"

import { persistFiles } from "src/lib/file"
import { FormFields } from "src/lib/form-fields"

import { createLoadingKeys } from "src/lib/loading"
import { reportError, reportUnhandledApiError } from "src/lib/report"
import { IAuthor } from "src/types/community-posts-and-comments/communityPostsAndComments"
import { IFormFieldValidation } from "src/types/form-fields/validation"

interface IFormFields {
    id: number | null
    authorId?: string | null
    text: string
    communityIds: number[]
    images: (IFile | null)[]
    pin: boolean
    accessType: string
    publishAt: Date | null
    pollId?: string | null
}

interface ICommunity {
    id: number
    name: string
}

const MAX_IMAGES = 3

const flutterBaseUrl = environment.FLUTTER_BASE_URL

export class CommunityPostsAndCommentsEditStore {
    static Context =
        React.createContext<CommunityPostsAndCommentsEditStore | null>(null)
    static LoadingKeys = createLoadingKeys("init", "submit")

    form = new FormFields<IFormFields>({
        id: null,
        authorId: null,
        text: "",
        communityIds: [],
        images: new Array(MAX_IMAGES).fill(null),
        pin: false,
        accessType: "",
        publishAt: null,
        pollId: null,
    })

    // Store the post status separately since it's only used for UI logic, not form submission
    postStatus: string = ""

    authors: IAuthor[] = []
    communities: ICommunity[] = []
    initialized = false

    get isEditing() {
        return this.form.get("id") != null
    }

    get isPublished() {
        return this.postStatus.toLowerCase() === "published"
    }

    /**
     * Checks if the form should be in read-only mode.
     * This centralizes all the disable logic that was previously scattered across components.
     */
    get isReadOnly() {
        return this.form.get("accessType") === "READ" || this.isPublished
    }

    /**
     * Determines if editing should be disabled for the form fields.
     * Used primarily for selection fields (author, community) that should be disabled
     * when either editing an existing post OR when the post is in read-only mode.
     */
    get editDisabled() {
        return this.isEditing || this.isReadOnly
    }

    constructor() {
        makeAutoObservable(this)
    }

    @loads(() => CommunityPostsAndCommentsEditStore.LoadingKeys.init)
    public async init(
        authors: IAuthor[],
        id?: number,
        accessGroupId?: number,
        originalPost?: community_PostWithAdminDetailsV2,
        newPollId?: number,
        isDuplicate?: boolean,
    ) {
        const communities = await this.loadCommunities(accessGroupId)
        this.setCommunities(communities)

        if (isDuplicate === true && originalPost != null) {
            // Handle duplicate post scenario
            this.setAuthors(authors)

            // Reset the status for duplicated posts
            this.postStatus = ""

            // Initialize the form with data from the original post but marked as new
            const text = (originalPost.text ?? "") + " (Copy)"
            this.form.init({
                id: null, // New post
                text,
                communityIds:
                    originalPost.community_id != null
                        ? [originalPost.community_id]
                        : [],
                images: this.imageUrlsToPersistedFiles(
                    originalPost.image_urls ?? [],
                ),
                pin: originalPost.pinned ?? false,
                accessType: originalPost.access_type ?? "",
                publishAt:
                    originalPost.publish_at != null
                        ? new Date(originalPost.publish_at)
                        : null,
                pollId: newPollId != null ? String(newPollId) : null,
            })
        } else if (id != null) {
            // Handle edit existing post
            const post = await CommunityAdminService.getV1AdminCommunityPost1({
                postId: id,
            })

            // Store the post status
            this.postStatus = post.status ?? ""

            this.setAuthors([{ id: "current", name: getAuthorName(post) }])
            this.form.init({
                id,
                text: post.text ?? "",
                communityIds:
                    post.community_id != null ? [post.community_id] : [],
                images: this.imageUrlsToPersistedFiles(post.image_urls ?? []),
                pin: post.pinned ?? false,
                accessType: post.access_type ?? "",
                publishAt:
                    post.publish_at != null ? new Date(post.publish_at) : null,
                pollId: post.poll_id != null ? String(post.poll_id) : null,
            })
        } else {
            // Handle new post
            // Reset status for new posts
            this.postStatus = ""

            this.setAuthors(authors)
            this.form.init({
                id: null,
                text: "",
                communityIds: [],
                images: new Array(MAX_IMAGES).fill(null),
                pin: false,
                accessType: "",
                publishAt: null,
                pollId: null,
            })
        }
        this.setInitialAuthor()

        this.setInitialized()
    }

    @loads(() => CommunityPostsAndCommentsEditStore.LoadingKeys.submit)
    public async submit() {
        this.validate()
        if (this.form.hasErrors()) {
            return
        }

        try {
            await this.form.catchErrors(async () => {
                const data = this.form.data
                if (data.id == null) {
                    await CommunityAdminService.postV1AdminCommunityPost({
                        request: {
                            author_name: this.getAuthorName(),
                            community_ids: data.communityIds,
                            image_urls: await this.getImagesAsPersistedUrls(),
                            pinned: data.pin,
                            text: data.text,
                            poll_id:
                                data.pollId != null
                                    ? parseInt(data.pollId)
                                    : undefined,
                            publish_at: data.publishAt?.toISOString(),
                        },
                    })

                    Channel.send({
                        name: "repository/updated",
                        payload: {
                            repository: "community-posts-and-comments",
                            action: "create",
                        },
                    })
                } else {
                    await CommunityAdminService.patchV1AdminCommunityPost({
                        postId: data.id,
                        request: {
                            // Even though it's not possible to change the
                            // author in the UI we need to add the author name
                            // to the payload otherwise it will be emptied.
                            author_name: this.getAuthorName(),
                            image_urls: await this.getImagesAsPersistedUrls(),
                            pinned: data.pin,
                            text: data.text,
                            poll_id:
                                data.pollId != null
                                    ? parseInt(data.pollId)
                                    : undefined,
                            publish_at: data.publishAt?.toISOString(),
                        },
                    })
                    Channel.send({
                        name: "repository/updated",
                        payload: {
                            repository: "community-posts-and-comments",
                            action: "update",
                        },
                    })
                }
            })
        } catch (e) {
            reportUnhandledApiError(e)
        }
    }

    public setImageAtIndex(image: IFile | null, index: number) {
        this.form.data.images[index] = image
    }

    public deselectCommunity(id: number) {
        const communities = this.form.get("communityIds")
        this.form.set(
            "communityIds",
            communities.filter((cid) => cid !== id),
        )
    }

    public getAuthorNameFromForm() {
        const id = this.form.get("authorId")
        const author = this.authors.find((author) => author.id === id)
        return author?.name
    }

    public getDetailsPreviewUrl = async () => {
        const detailsPreviewUrl = flutterBaseUrl + "community-preview?base64="
        const data = this.form.data

        const imageUrls = await this.getImagesAsPersistedUrls()

        try {
            let url = await PreviewService.postV1PreviewCommunityPost({
                request: {
                    author_name: this.getAuthorNameFromForm(),
                    category_id: 0,
                    community_ids: [...data.communityIds],
                    image_urls: imageUrls,
                    pinned: false,
                    text: data.text,
                },
            })
            const previewObject = JSON.parse(window.atob(url))
            previewObject.original_response = {
                post: {
                    ...previewObject.original_response,
                },
            }

            if (data.pollId != null) {
                const poll = await PollAdminService.getV1AdminPoll({
                    pollId: parseInt(data.pollId),
                })

                Object.assign(previewObject.original_response, {
                    poll: {
                        poll_id: parseInt(data.pollId),
                        close_at: poll.close_at,
                        status: poll.status,
                    },
                })
            }
            url = window.btoa(JSON.stringify(previewObject))

            return `${detailsPreviewUrl}${encodeURIComponent(url)}`
        } catch (error) {
            reportUnhandledApiError(error)
            return ""
        }
    }

    private async loadCommunities(accessGroupId?: number) {
        try {
            return await CommunityAdminService.getV1AdminCommunity({
                accessGroupId:
                    accessGroupId !== DEFAULT_ACCESS_GROUP.id
                        ? accessGroupId
                        : undefined,
            })
        } catch (e) {
            reportUnhandledApiError(e)
            return []
        }
    }

    private setCommunities(communities: community_CommunityForAdmin[]) {
        this.communities = communities.map((community) => ({
            id: community.community_id ?? -1,
            // Disable this rule here as we want to use `name` if
            // `internal_name` is either null or an empty string.
            // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
            name: community.internal_name || community.name || "",
        }))
    }

    private setInitialized() {
        this.initialized = true
    }

    private async getImagesAsPersistedUrls(): Promise<string[]> {
        try {
            const images = this.form
                .get("images")
                .filter(
                    (image): image is Exclude<typeof image, null> =>
                        image != null,
                )
            const persistedImages = await persistFiles(images, "image")
            return persistedImages.map((image) => image.url)
        } catch (e) {
            reportError(
                `community-post-detail-modal.error-failed-to-persist-files`,
                e,
            )
            throw e
        }
    }

    private setAuthors(authors: IAuthor[]) {
        this.authors = authors
    }

    private getInitialAuthor() {
        if (this.authors.length > 0) {
            return this.authors[0].id
        }
        return null
    }

    private setInitialAuthor() {
        // Get local stored author
        const localAuthorId = this.getAuthorFromLocalStore()
        // Validate if the stored author is present in the author list
        const authorExist = this.authors.find(
            (author) => author.id === localAuthorId,
        )

        if (authorExist !== undefined && localAuthorId != null) {
            this.form.set("authorId", localAuthorId)
        } else if (authorExist === undefined || this.authors.length > 0) {
            this.setAuthorIdToLocalStore(this.authors[0].id)
            this.form.set("authorId", this.authors[0].id)
        }
    }

    public getAuthorName() {
        const id = this.form.get("authorId")
        const author = this.authors.find((author) => author.id === id)
        return author?.name
    }

    private imageUrlsToPersistedFiles(
        urls: string[],
    ): (IPersistedFile | null)[] {
        const files: (IPersistedFile | null)[] = urls.map(
            (url): IPersistedFile => ({
                type: "image",
                name: url,
                url,
            }),
        )
        for (const _ in range(Math.max(0, MAX_IMAGES - files.length))) {
            files.push(null)
        }
        return files
    }

    private validate() {
        this.form.clearErrors()
        const validationData: IFormFieldValidation<IFormFields>[] = [
            {
                field: "communityIds",
                errorMessage: t`community-post-detail-modal.error-must-select-atleast-one-community`,
                validate: (value) => (value as number[]).length > 0,
            },
            {
                field: "text",
            },
        ]
        if (this.form.data.publishAt != null) {
            validationData.push({
                field: "publishAt",
                errorMessage: t`errors.date_in_past`,
                validate: (value) =>
                    value != null && !isDateInPast(value as Date),
            })
        }
        this.form.validateRequiredFields(validationData)
    }

    private getAuthorFromLocalStore() {
        return window.localStorage.getItem("authorId")
    }

    public setAuthorIdToLocalStore(authorId: string) {
        window.localStorage.setItem("authorId", authorId)
    }
}
